import React, { FC, useEffect, useState } from 'react';
import Table from '@components/table';
import styles from './styles.module.scss';
import Status from '@components/common/status';
import Typography from '@components/common/typography';
import Drawer from '@components/drawer';
import PriceStatus from '@components/common/price_status';
import { useTransactions } from '@hooks/api/useTransactions';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType,
    TTransactionLite
} from '@xeppt/xeppt-sdk/types/transaction';
import {
    getTransactionAction,
    getTransactionMethod,
    getTransactionStatus,
    getTransactionStatusColor
} from '@utils/index';
import moment from 'moment';
import { transactionsMethods, transactionsStatuses, transactionsType } from '@const/index';
import { Icon } from '@components/icons';
import Dropdown from '@components/common/dropdown';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import { TConsumerCardTransactionResponseWithReferenceCode } from '@reference_code';
import Input from '@components/common/input';

interface IProps {
    cardId: string;
}

const CardTransactionsSection: FC<IProps> = () => {
    const { id: customer_id } = useParams<{ id: string }>();
    const [cardId] = useQueryParams(['cardId']);
    const [sort, setSort] = useState<{ created_at: 'ASC' | 'DESC' }>({
        created_at: 'ASC'
    });
    const [currentTransaction, setCurrentTransaction] =
        useState<TConsumerCardTransactionResponseWithReferenceCode>();
    const { cardTransactions, cardTransactionsData, handleRequestCardTransaction } =
        useTransactions({
            customer_id,
            card_id: cardId || '',
            isInitialTransactions: false
        });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const removeQueryParams = (params: string[]) => {
        const searchParams = new URLSearchParams(location.search);
        params.forEach((param) => searchParams.delete(param));
        navigate({
            pathname: location.pathname,
            search: searchParams.toString()
        });
    };

    useEffect(() => {
        if (cardId) {
            handleRequestCardTransaction();
        }
    }, [cardId]);

    const columns = [
        {
            label: 'Date and Time',
            key: 'created_at',
            isSort: true,
            render: (value: string) => moment(value).format('MM.DD.YYYY HH:mm')
        },
        {
            label: 'Transaction ID',
            key: 'id'
        },
        {
            label: 'Transaction Method',
            key: 'method',
            filterOptions: transactionsMethods,
            render: (value: ETransactionMethod) => getTransactionMethod(value) //
        },
        { label: 'Consumer Name', key: 'user_name' },
        { label: 'Source', key: 'source' },
        {
            label: 'Action',
            key: 'action',
            filterOptions: transactionsType,
            render: (_: ETransactionAction, data: TTransactionLite) =>
                getTransactionAction(data.action)
        },
        {
            label: 'Status',
            key: 'status',
            filterOptions: transactionsStatuses,
            render: (value: ETransactionStatus) => (
                <Status variant={getTransactionStatusColor(value)}>
                    {getTransactionStatus(value)}
                </Status>
            )
        },
        { label: 'Currency', key: 'currency' },
        {
            label: 'Amount',
            key: 'amount',
            render: (value: number, data: TTransactionLite) => (
                <PriceStatus
                    isNegative={data.type === ETransactionType.CREDIT}
                    amount={value || 0}
                    currency="$"
                />
            )
        },
        {
            label: 'Actions',
            key: 'id',
            render: (id: string) => (
                <Dropdown
                    items={[
                        {
                            label: 'Details',
                            onClick: () => {
                                setIsDrawerOpen(true);
                                setCurrentTransaction(
                                    cardTransactionsData?.data?.find(
                                        (item) => item.transaction.id === id
                                    )
                                );
                            }
                        }
                    ]}
                />
            )
        }
    ];

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <i onClick={() => removeQueryParams(['cardId'])}>
                        <Icon name="arrow_left" width={16} height={16} />
                        Back
                    </i>
                    <Typography variant="h4">Transactions</Typography>
                    <Typography>{cardTransactionsData?.total} records found</Typography>
                </div>
            </div>
            <Table
                //@ts-ignore
                columns={columns}
                rows={cardTransactions.map((item) => item.transaction)}
                sort={{ key: 'created_at', value: sort.created_at }}
                onSortChange={() => {
                    setSort((state) => ({
                        created_at: state.created_at === 'DESC' ? 'ASC' : 'DESC'
                    }));
                }}
                emptyDescription="Transactions list is empty"
            />
            <Drawer
                className={styles.drawer}
                isOpen={isDrawerOpen && !!currentTransaction}
                width={670}
                onClose={() => {
                    setIsDrawerOpen(false);
                    setCurrentTransaction(undefined);
                }}>
                <Typography className={styles.title} variant="h5">
                    Transaction details
                </Typography>
                <div className={styles.content}>
                    <Input
                        readOnly
                        label="Transaction ID"
                        value={currentTransaction?.transaction?.id}
                    />
                    <Input
                        readOnly
                        label="Reference code"
                        value={currentTransaction?.transaction?.reference_code ?? '—'}
                    />
                    <Input
                        readOnly
                        label="Type"
                        value={
                            currentTransaction?.transaction.type === ETransactionType.CREDIT
                                ? 'Credit'
                                : 'Debit'
                        }
                    />
                    <Input
                        readOnly
                        label="Method"
                        value={getTransactionMethod(currentTransaction?.transaction?.method)}
                    />
                    <Input
                        readOnly
                        label="Action"
                        value={getTransactionAction(currentTransaction?.transaction?.action)}
                    />
                    <Input
                        readOnly
                        label="Amount"
                        value={currentTransaction?.transaction?.amount.toString()}
                    />
                    <Input
                        readOnly
                        label="Currency"
                        value={currentTransaction?.transaction?.currency}
                    />
                    <Input
                        readOnly
                        label="Fee"
                        value={currentTransaction?.transaction?.fee.toString()}
                    />
                    <Input
                        readOnly
                        label="Points"
                        value={currentTransaction?.transaction?.points.toString()}
                    />
                    <Input
                        readOnly
                        label="Source"
                        value={currentTransaction?.transaction?.source}
                    />
                    <Input
                        readOnly
                        label="Destination"
                        value={currentTransaction?.transaction?.destination}
                    />
                    <Input
                        readOnly
                        label="Created At"
                        value={
                            currentTransaction?.transaction?.created_at
                                ? moment(currentTransaction?.transaction?.created_at).format(
                                      'MM.DD.YYYY HH:mm'
                                  )
                                : '-'
                        }
                    />
                    <Input
                        readOnly
                        label="Updated At"
                        value={
                            //@ts-ignore
                            currentTransaction?.transaction?.updated_at
                                ? //@ts-ignore
                                  moment(currentTransaction?.transaction?.updated_at).format(
                                      'MM.DD.YYYY HH:mm'
                                  )
                                : '-'
                        }
                    />
                </div>
                <Typography className={styles.title} variant="h5">
                    Processor transaction details
                </Typography>
                <div className={styles.content}>
                    <Input
                        readOnly
                        label="Transaction ID"
                        value={currentTransaction?.transaction_id?.toString()}
                    />
                    <Input
                        readOnly
                        label="Card ID"
                        value={currentTransaction?.card_id?.toString()}
                    />
                    <Input
                        readOnly
                        label="Merchant name"
                        value={currentTransaction?.merchant_name}
                    />
                    <Input
                        readOnly
                        label="Merchant city"
                        value={currentTransaction?.merchant_city}
                    />
                    <Input
                        readOnly
                        label="Merchant country"
                        value={currentTransaction?.merchant_country}
                    />
                    <Input readOnly label="Amount" value={currentTransaction?.amount?.toFixed(2)} />
                    <Input
                        readOnly
                        label="Response code"
                        value={currentTransaction?.response_code}
                    />
                    <Input
                        readOnly
                        label="Customer number"
                        value={currentTransaction?.customer_number}
                    />
                    <Input
                        readOnly
                        label="Is succeeded"
                        value={currentTransaction?.is_succeeded ? 'Yes' : 'No'}
                    />
                    <Input
                        readOnly
                        label="Is financial transaction"
                        value={currentTransaction?.is_financial_transaction ? 'Yes' : 'No'}
                    />
                    <Input
                        readOnly
                        label="Settlement status"
                        value={currentTransaction?.settlement_status}
                    />
                    <Input
                        readOnly
                        label="Card number"
                        value={currentTransaction?.masked_card_number}
                    />
                    <Input
                        readOnly
                        label="Transaction type"
                        value={currentTransaction?.transaction_type}
                    />
                    <Input
                        readOnly
                        label="Transaction type ID"
                        value={currentTransaction?.transaction_type_id?.toString()}
                    />
                    <Input
                        readOnly
                        label="Transaction type code"
                        value={currentTransaction?.transaction_type_code?.toString()}
                    />
                    <Input
                        readOnly
                        label="Settlement amount"
                        value={currentTransaction?.settlement_amount?.toString()}
                    />
                    <Input
                        readOnly
                        label="Settlement currency code"
                        value={currentTransaction?.settlement_currency_code?.toString()}
                    />
                    <Input
                        readOnly
                        label="Created At"
                        value={
                            currentTransaction?.settlement_date
                                ? moment(currentTransaction?.settlement_date).format(
                                      'MM.DD.YYYY HH:mm'
                                  )
                                : '-'
                        }
                    />
                    <Input readOnly label="Description" value={currentTransaction?.description} />
                    <Input
                        readOnly
                        label="Reference number"
                        value={currentTransaction?.reference_number}
                    />
                    <Input readOnly label="Chanel name" value={currentTransaction?.channel_name} />
                    <Input
                        readOnly
                        label="Card owner name"
                        value={currentTransaction?.card_owner_name}
                    />
                    <Input
                        readOnly
                        label="Card transaction source"
                        value={currentTransaction?.card_transaction_source}
                    />
                    <Input
                        readOnly
                        label="Terminal type"
                        value={currentTransaction?.terminal_type}
                    />{' '}
                    <Input
                        readOnly
                        label="Terminal type"
                        value={currentTransaction?.terminal_type}
                    />
                    <Input
                        readOnly
                        label="Fee amount"
                        value={currentTransaction?.fee_amount?.toFixed(2)}
                    />
                    <Input
                        readOnly
                        label="Is reversed"
                        value={currentTransaction?.is_reversed ? 'Yes' : 'No'}
                    />
                    <Input
                        readOnly
                        label="Reversed date time"
                        value={
                            currentTransaction?.reversed_date_time
                                ? moment(currentTransaction?.reversed_date_time).format(
                                      'MM.DD.YYYY HH:mm'
                                  )
                                : '-'
                        }
                    />
                    <Input
                        readOnly
                        label="Has incremental authorization"
                        value={currentTransaction?.has_incremental_authorization ? 'Yes' : 'No'}
                    />
                    <Input
                        readOnly
                        label="Authorization code"
                        value={currentTransaction?.authorzation_code}
                    />
                    <Input
                        readOnly
                        label="Card token number"
                        value={currentTransaction?.card_token_number}
                    />
                    <Input
                        readOnly
                        label="Billing amount"
                        value={currentTransaction?.billing_amount?.toFixed(2)}
                    />
                    <Input
                        readOnly
                        label="Cardholder currency code"
                        value={currentTransaction?.cardholder_currency_code}
                    />
                    <Input
                        readOnly
                        label="Card brand name"
                        value={currentTransaction?.card_brand_name}
                    />
                    <Input
                        readOnly
                        label="Available credit limit"
                        value={currentTransaction?.available_credit_limit?.toFixed(2)}
                    />
                    <Input
                        readOnly
                        label="Wallet balance"
                        value={currentTransaction?.wallet_balance?.toFixed(2)}
                    />
                    <Input
                        readOnly
                        label="Application network"
                        value={currentTransaction?.application_network}
                    />
                    <Input
                        readOnly
                        label="Transaction entry type"
                        value={currentTransaction?.transaction_entry_type_desc}
                    />
                    <Input
                        readOnly
                        label="Transaction type description"
                        value={currentTransaction?.transaction_type_desc}
                    />
                    <Input readOnly label="MTI" value={currentTransaction?.mti?.toString()} />
                    <Input
                        readOnly
                        label="Transaction unique ID"
                        value={currentTransaction?.transaction_unique_id}
                    />
                </div>
            </Drawer>
        </div>
    );
};

export default CardTransactionsSection;
